import React, { useState } from 'react';
import { Cake, Gift, PartyPopper, Copy, Check, X } from 'lucide-react';
import { claimBirthdayReward } from '../services/apiClient';

const CONFETTI = ['#ef4444', '#3b82f6', '#fde047', '#22c55e', '#a855f7', '#f97316'];

export default function BirthdayCelebrationPage({ status, onClose }) {
  const [claimed, setClaimed] = useState(Boolean(status?.alreadyClaimed));
  const [code, setCode] = useState(status?.rewardCode || null);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const firstName = status?.firstName || 'there';

  async function handleClaim() {
    setClaiming(true);
    setError('');
    const result = await claimBirthdayReward();
    setClaiming(false);
    if (result?.ok) {
      setClaimed(true);
      setCode(result.code || null);
    } else {
      setError(result?.message || 'Could not claim right now. Please try again.');
      if (result?.error === 'ALREADY_CLAIMED') setClaimed(true);
    }
  }

  function copyCode() {
    if (!code) return;
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable; the code is visible to copy manually */
    }
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <style>{`
        @keyframes bmConfettiFall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(105vh) rotate(540deg); opacity: 0.9; }
        }
        @media (prefers-reduced-motion: reduce) { .bm-confetti { animation: none !important; display: none; } }
      `}</style>

      {/* Confetti */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {Array.from({ length: 28 }).map((_, i) => (
          <span
            key={i}
            className="bm-confetti absolute top-0 rounded-sm"
            style={{
              left: `${(i * 3.5) % 100}%`,
              width: `${6 + (i % 3) * 3}px`,
              height: `${10 + (i % 3) * 4}px`,
              background: CONFETTI[i % CONFETTI.length],
              animation: `bmConfettiFall ${3 + (i % 4) * 0.8}s linear ${(i % 6) * 0.4}s infinite`,
            }}
          />
        ))}
      </div>

      <div className="relative w-full max-w-md rounded-3xl border border-mentor-border/70 bg-mentor-surface p-7 text-center shadow-2xl">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 rounded-lg p-1.5 text-mentor-muted hover:bg-mentor-bg/60"><X size={18} /></button>

        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-mentor-accent/15 border border-mentor-accent/30">
          <Cake size={32} className="text-mentor-accent" />
        </div>

        <h1 className="font-display font-black text-3xl text-mentor-text mb-1">Happy Birthday, {firstName}!</h1>
        <p className="text-mentor-muted text-sm mb-6 flex items-center justify-center gap-1.5">
          <PartyPopper size={15} className="text-mentor-accent" /> From everyone at Bond Mentor
        </p>

        {!claimed ? (
          <>
            <p className="text-mentor-text/90 mb-5">To celebrate, here&rsquo;s <span className="font-display font-bold">one free month</span> on us — our gift for your special day.</p>
            {error && <p className="mb-3 rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-200">{error}</p>}
            <button type="button" onClick={handleClaim} disabled={claiming}
              className="w-full justify-center inline-flex items-center gap-2 rounded-xl bg-mentor-accent px-5 py-3 font-display font-bold text-white hover:bg-mentor-accent/90 disabled:opacity-60">
              <Gift size={17} /> {claiming ? 'Applying…' : 'Claim My Free Month'}
            </button>
          </>
        ) : (
          <>
            <div className="rounded-2xl border border-green-400/30 bg-green-400/10 p-4 mb-4">
              <p className="text-green-200 font-display font-bold mb-2 flex items-center justify-center gap-1.5"><Check size={16} /> Your free month is applied</p>
              {code && (
                <button type="button" onClick={copyCode} className="inline-flex items-center gap-2 rounded-lg border border-green-400/30 bg-mentor-bg/40 px-3 py-1.5 font-mono text-sm text-mentor-text hover:border-green-400/60">
                  {code} {copied ? <Check size={13} className="text-green-300" /> : <Copy size={13} className="text-mentor-muted" />}
                </button>
              )}
              <p className="text-[11px] text-mentor-muted mt-2">Keep this code for your records. Your complimentary month is on your account.</p>
            </div>
          </>
        )}

        <button type="button" onClick={onClose} className="mt-5 text-sm text-mentor-muted hover:text-mentor-text underline-offset-4 hover:underline">
          Continue to Bond Mentor
        </button>
      </div>
    </div>
  );
}
